import maxceiEmblem from "@assets/images/maxcei-emblem.png";
import { useUser } from "@dashboard/auth";
import { useLegacyThemeHandler } from "@dashboard/components/Sidebar/user/Controls";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import { LogOut, Moon, Sun } from "lucide-react";
import { ReactNode } from "react";
import { useIntl } from "react-intl";
import { NavLink } from "react-router-dom";

import { operatorMessages } from "./messages";
import { OperatorModule } from "./modules";

// Staff created with only a username get this placeholder email (backend
// USERNAME_PLACEHOLDER_EMAIL_DOMAIN); show the username part instead.
const PLACEHOLDER_EMAIL_SUFFIX = "@users.invalid";

const displayEmail = (email = "") =>
  email.endsWith(PLACEHOLDER_EMAIL_SUFFIX)
    ? email.slice(0, -PLACEHOLDER_EMAIL_SUFFIX.length)
    : email;

interface OperatorLayoutProps {
  modules: Pick<OperatorModule, "id" | "path" | "title">[];
  children: ReactNode;
  // Shown after "MAXCEI ·"; the employee portal reuses this layout.
  platformName?: string;
  // Extra top-bar controls (e.g. the portal's language switch).
  actions?: ReactNode;
}

// The dashboard's chrome without the sidebar: a slim top bar with the
// operator's modules, their name, theme toggle and log out. Pages rendered
// inside are the regular dashboard sections, so the look is identical.
export const OperatorLayout = ({
  modules,
  children,
  platformName,
  actions,
}: OperatorLayoutProps) => {
  const intl = useIntl();
  const { user, logout } = useUser();
  const { changeTheme, theme } = useLegacyThemeHandler();
  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") || displayEmail(user?.email);

  return (
    <Box display="flex" flexDirection="column" __minHeight="100vh" backgroundColor="default1">
      <Box
        as="header"
        display="flex"
        alignItems="center"
        gap={4}
        paddingX={6}
        paddingY={3}
        borderBottomWidth={1}
        borderBottomStyle="solid"
        borderColor="default1"
      >
        <Box display="flex" alignItems="center" gap={3}>
          <Box as="img" src={maxceiEmblem} alt="MAXCEI" width={8} height={8} />
          <Text size={4} fontWeight="bold">
            MAXCEI · {platformName ?? intl.formatMessage(operatorMessages.platformName)}
          </Text>
        </Box>
        {modules.length > 1 && (
          <Box as="nav" display="flex" gap={1} flexWrap="wrap">
            {modules.map(module => (
              <NavLink
                key={module.id}
                to={module.path}
                style={{ textDecoration: "none" }}
                activeStyle={{ fontWeight: 700 }}
              >
                <Button variant="tertiary" size="small" as="span">
                  {intl.formatMessage(module.title)}
                </Button>
              </NavLink>
            ))}
          </Box>
        )}
        <Box __flex={1} />
        <Text size={2} color="default2" display={{ mobile: "none", tablet: "block" }}>
          {displayName}
        </Text>
        {actions}
        <Button
          variant="tertiary"
          size="small"
          icon={theme === "defaultLight" ? <Moon size={16} /> : <Sun size={16} />}
          onClick={changeTheme}
          title={intl.formatMessage(operatorMessages.toggleTheme)}
          aria-label={intl.formatMessage(operatorMessages.toggleTheme)}
        />
        <Button
          variant="secondary"
          size="small"
          onClick={() => logout?.()}
          data-test-id="operator-log-out"
        >
          <LogOut size={16} />
          {intl.formatMessage(operatorMessages.logout)}
        </Button>
      </Box>
      <Box as="main" __flex={1}>
        {children}
      </Box>
    </Box>
  );
};
